import { Component, type ErrorInfo, type ReactNode } from 'react';

import styles from './ErrorBoundary.module.css';

/**
 * The error boundary — docs/ARCHITECTURE.md §3, recon §4.2, SPEC §7.
 *
 * A class, because `getDerivedStateFromError` has no hook equivalent; this is the one class
 * component in the product.
 *
 * `resetKeys` is the part that makes it useful rather than decorative. The legacy boundary had
 * none, so its "Try Again" button re-rendered the same broken subtree with the same broken
 * props and threw again immediately — a button that looks like an escape and is not one. Here,
 * when any key in `resetKeys` changes while the boundary is caught, the error is cleared: the
 * inputs that produced the crash are gone, so the subtree deserves another go.
 *
 * The stack is DEV-ONLY and behind `<details>`: in production a user sees the localised
 * fallback and nothing else, and there is NO error-reporting SDK — no telemetry is the product
 * promise, not a preference (ADR-0001, ADR-0023). `console.error` is the entire reporting
 * strategy.
 */

export interface ErrorBoundaryRenderProps {
  readonly error: Error;
  /** Clears the caught error and re-renders the children. */
  readonly reset: () => void;
}

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /**
   * Required, and a function. There is no built-in fallback on purpose: every string on screen
   * comes from i18next, and `ui/**` does not translate (ADR-0003, ADR-0018). A default would
   * be hardcoded English shipped to production.
   */
  readonly fallback: (props: ErrorBoundaryRenderProps) => ReactNode;
  readonly onError?: (error: Error, info: ErrorInfo) => void;
  /** Any change while caught clears the error. Compared item-by-item with `Object.is`. */
  readonly resetKeys?: readonly unknown[];
  /** Summary of the DEV-only stack disclosure. Never reaches a production build. */
  readonly detailsLabel?: string;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

function changed(a: readonly unknown[] | undefined, b: readonly unknown[] | undefined): boolean {
  if (a === b) return false;
  if (a === undefined || b === undefined) return true;
  if (a.length !== b.length) return true;
  return a.some((value, index) => !Object.is(value, b[index]));
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    // A thrown non-Error (`throw 'boom'`) is legal JavaScript and would otherwise crash the
    // fallback itself while it tried to read `.message`.
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- the entire reporting strategy; see ADR-0023.
    console.error('[jsonfmt] uncaught error in a React subtree', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.error !== null && changed(previous.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <>
        {this.props.fallback({ error, reset: this.reset })}
        {import.meta.env.DEV && (
          <details className={styles.details}>
            <summary className={styles.summary}>
              {this.props.detailsLabel ?? 'Technical details'}
            </summary>
            <pre className={styles.stack}>{error.stack ?? error.message}</pre>
          </details>
        )}
      </>
    );
  }
}

export default ErrorBoundary;
